<script setup lang="ts">
import {ArrowRight, Check, Loader2, MessageCircle, MessageSquare, Shield, TriangleAlert} from "@lucide/vue";
import {NotificationTemplate as INotificationTemplate} from "@/notification-template/interfaces";
import FrenchPhoneInput from "@/rcs/components/try-dialog/shared/FrenchPhoneInput.vue";
import {ref, watch} from "vue";
import CreditBalance from "@/rcs/components/try-dialog/shared/CreditBalance.vue";
import {Button} from "@/core/components/ui/button";
import TemplatePreview from "@/rcs/components/try-dialog/shared/TemplatePreview.vue";
import StepDialogHeader from "@/rcs/components/try-dialog/shared/StepDialogHeader.vue";
import {rcsApi} from "@/container";

const props = defineProps<{
    template: INotificationTemplate;
    phone?: string;
    sending: boolean;
}>();

const emit = defineEmits<{
    (e: 'cancel'): void;
    (e: 'send', phone: string, isCapable: boolean): void;
    (e: 'update:phone', phone: string): void;
}>();

const phone = ref(props.phone ?? '');
const capabilityState = ref<'idle' | 'loading' | 'capable' | 'not-capable'>('idle');

watch(phone, (newPhone) => {
    if (!newPhone) {
        capabilityState.value = 'idle';
        return;
    }

    capabilityState.value = 'loading';

    rcsApi().deviceCapabilities(newPhone)
        .then((response) => {
            capabilityState.value = response.is_capable ? 'capable' : 'not-capable';
        })
        .catch(() => {
            capabilityState.value = 'not-capable';
        });
}, {immediate: true});

const onSend = () => {
    if (!phone.value) {
        return;
    }

    emit('send', phone.value, capabilityState.value === 'capable');
};
</script>

<template>
  <div class="flex flex-col gap-6">
    <div class="flex flex-col gap-3">
      <StepDialogHeader :icon="MessageCircle" />
      <div class="flex flex-col gap-1">
        <h3>Recevez ce message sur votre mobile</h3>
        <p class="text-muted-foreground text-sm">
          Indiquez votre numéro pour découvrir ce message exactement comme le verront vos patients
        </p>
      </div>
    </div>

    <TemplatePreview :template="template" />

    <div class="flex flex-col gap-3">
      <FrenchPhoneInput v-model="phone" @update:model-value="emit('update:phone', $event)" />

      <div v-if="capabilityState === 'loading'" class="flex gap-1.5 items-center text-muted-foreground text-sm">
        <Loader2 class="size-4 shrink-0 animate-spin" />
        <p class="leading-tight">
          Vérification de la compatibilité RCS...
        </p>
      </div>

      <div v-else-if="capabilityState === 'capable'" class="flex gap-1.5 items-start text-sm text-green-700">
        <Check class="size-4 shrink-0" />
        <p class="leading-tight font-medium">
          Ce mobile est compatible RCS — vous recevrez un message enrichi
        </p>
      </div>

      <div v-else-if="capabilityState === 'not-capable'" class="flex gap-1.5 items-start text-sm text-amber-700">
        <TriangleAlert class="size-4 shrink-0" />
        <p class="leading-tight">
          Ce mobile n'est pas compatible RCS —
          <span class="font-medium">vous recevrez un SMS correspondant à la place</span>
        </p>
      </div>

      <div v-else class="flex gap-1.5 items-center">
        <Shield class="size-4 shrink-0 text-muted-foreground" />
        <p class="text-muted-foreground text-sm leading-tight">
          La compatibilité RCS sera vérifiée automatiquement
        </p>
      </div>

      <CreditBalance :cost="template.credits" />
    </div>

    <div class="flex justify-end gap-3">
      <Button variant="outline" @click="$emit('cancel')">
        Annuler
      </Button>
      <Button
        class="bg-primary"
        :disabled="!phone || capabilityState === 'loading' || sending"
        @click="onSend"
      >
        <MessageSquare v-if="capabilityState === 'not-capable'" />
        <ArrowRight v-else />
        <span class="leading-tight">
          {{ capabilityState === 'not-capable' ? 'Recevoir le SMS' : 'Recevoir le message' }}
        </span>
      </Button>
    </div>
  </div>
</template>

<style scoped>

</style>
